import { useFormContext } from 'react-hook-form';
import { lt } from '@mews/framework';
import { Button, IconName, Stack, Typography } from '@optimus-web/core';
import { useDialogForm } from '@mews-commander/form';
import { FORM_FIELD_PATHS } from '../../constants';
import { GenerateContractFormValues } from '../../types';
import { SelectedItemCard } from '../SelectedItemCard';
import { useCurrentEnterpriseAttachments } from '../../../../common/hooks/useCurrentEnterpriseAttachments';
import { SelectAttachmentForm } from './SelectAttachmentForm';

export const SelectAttachmentSection = () => {
    const attachmentsData = useCurrentEnterpriseAttachments();

    const { watch, setValue } = useFormContext<GenerateContractFormValues>();
    const selectedTemplates = watch(FORM_FIELD_PATHS.selectedTemplates);
    const isAtLeastOneTemplateSelected = Object.keys(selectedTemplates).length > 0;

    const selectedAttachments = watch(FORM_FIELD_PATHS.selectedAttachments);

    const handleSelectedAttachmentRemove = (attachmentId: string) => {
        const updatedTemplates = selectedAttachments?.filter(({ id }) => id !== attachmentId);
        setValue(FORM_FIELD_PATHS.selectedAttachments, updatedTemplates);
    };

    const handleAddAttachment = (ids: string[]) => {
        const attachments = ids.map((id) => ({ id, expirationDate: undefined, expirationDateEnabled: false }));
        setValue(FORM_FIELD_PATHS.selectedAttachments, attachments);
    };

    const [renderSelectForm, toggleSelectForm] = useDialogForm({
        title: lt('ContractsAttachmentsLabel'),
        renderForm: (props) => (
            <SelectAttachmentForm
                previouslySelected={selectedAttachments?.map(({ id }) => id)}
                handleAddAttachment={handleAddAttachment}
                {...props}
            />
        ),
    });

    return (
        <>
            <Stack vertical spacing="200">
                <Stack vertical spacing="100">
                    <Typography textStyle="titleMediumStrong" as="h2">
                        {lt('ContractsAttachmentsLabel')}
                    </Typography>
                    <Typography textStyle="bodyMedium" as="div">
                        {lt('ContractsGenerateContractAddAttachmentDescription')}
                    </Typography>
                </Stack>

                {(selectedAttachments || []).map((attachment, index) => {
                    const name = attachmentsData?.find(({ id }) => attachment.id === id)?.customName || '';
                    return (
                        <SelectedItemCard
                            key={attachment.id}
                            name={name}
                            expirationDateFieldName={`${FORM_FIELD_PATHS.selectedAttachments}.${index}.expirationDate`}
                            expirationDateEnabledFieldName={`${FORM_FIELD_PATHS.selectedAttachments}.${index}.expirationDateEnabled`}
                            onRemove={() => handleSelectedAttachmentRemove(attachment.id)}
                        />
                    );
                })}

                <Stack distribution="space-around">
                    <Button
                        disabled={!isAtLeastOneTemplateSelected}
                        icon={IconName.PlusSimple}
                        variant="secondary"
                        type="button"
                        onClick={toggleSelectForm}
                    >
                        {lt('ContractsAddAttachmentLabel')}
                    </Button>
                </Stack>
            </Stack>
            {renderSelectForm()}
        </>
    );
};
